import styled, { keyframes, css } from 'styled-components';

/* ── paleta do design system (calendário/turma/funcionário) ── */
const TEAL = 'var(--teal)';
const TEAL_TEXT = 'var(--teal-text, var(--teal))';
const TEAL_LIGHT = 'var(--teal-light)';
const TEAL_SOFT = 'var(--teal-soft)';
const GRAY_BG = 'var(--bg)';
const GRAY_LINE = 'var(--line)';
const GRAY_TEXT = 'var(--text-muted)';
const GREEN = 'var(--green)';
const GREEN_SOFT = 'var(--green-soft)';
const RED = 'var(--red)';
const RED_SOFT = 'var(--red-soft)';
const AMBER = 'var(--amber, #d97706)';
const AMBER_SOFT = '#fdf2e0';
const BLUE = '#2a6fb0';
const BLUE_SOFT = '#e7f0f9';

const CORAL = 'var(--coral)';
const TEAL_GLOW = 'var(--teal-glow)';

const bp = { desktop: '1024px', tablet: '768px', mobile: '540px', small: '420px' };

/* ── animações ── */
const expand = keyframes`
  from { opacity: 0; transform: translateY(-6px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0);    max-height: 9999px; }
`;
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── botão base ── */
const btnBase = css`
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  border-radius: 10px;
  padding: 10px 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: transform 0.12s ease, filter 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
  &:hover { filter: brightness(1.05); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.55; cursor: not-allowed; transform: none; filter: none; }
`;

/* ── Container geral ── */
export const Container = styled.div`
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
  box-sizing: border-box;
  background:
    radial-gradient(1200px 400px at 50% -120px, var(--bg-glow) 0%, transparent 70%),
    ${GRAY_BG};
  padding: 16px;
  @media (max-width: ${bp.tablet}) { padding: 12px; }
  @media (max-width: ${bp.mobile}) { padding: 8px; }
`;

export const ContainerDivs = styled.div`
  width: 100%;
  max-width: 960px;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
  @media (max-width: ${bp.mobile}) { gap: 10px; }
`;

/* ── rótulo de seção ── */
export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 2px 0;
  color: ${GRAY_TEXT};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  &::after { content: ''; flex: 1; height: 1px; background: ${GRAY_LINE}; }
`;

/* ── HERO / Card (Emp) ──
   O perfil principal vira hero; os cards de turma reusam o mesmo componente
   mas com o modificador $plain para ficarem brancos. */
export const Emp = styled.div`
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  border-radius: 16px;
  animation: ${fadeUp} 0.35s ease backwards;

  ${({ $plain }) => $plain
    ? css`
        background: var(--surface);
        border: 1px solid ${GRAY_LINE};
        padding: 16px 18px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        transition: box-shadow 0.18s ease;
        &:hover { box-shadow: 0 5px 16px rgba(0, 0, 0, 0.05); }
      `
    : css`
        background: linear-gradient(135deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%);
        color: #fff;
        padding: 24px 26px;
        box-shadow: 0 8px 24px ${TEAL_GLOW};
        &::before, &::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.06);
          pointer-events: none;
        }
        &::before { width: 200px; height: 200px; top: -90px; right: -50px; }
        &::after  { width: 140px; height: 140px; bottom: -80px; right: 120px; }
      `}
  @media (max-width: ${bp.mobile}) { padding: ${({ $plain }) => ($plain ? '14px' : '20px 18px')}; border-radius: 14px; }
`;

export const EmployeeInfo = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Pro = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  width: 100%;
  @media (max-width: ${bp.mobile}) { gap: 14px; }
`;

export const ProfilePhoto = styled.div`
  width: 78px;
  height: 78px;
  min-width: 78px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  border: 2px solid rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 27px;
  color: #fff;
  text-transform: uppercase;
  backdrop-filter: blur(4px);
  @media (max-width: ${bp.mobile}) { width: 62px; height: 62px; min-width: 62px; font-size: 22px; }
`;

/* Dentro do hero, o ProfileInfo organiza nome + grade de detalhes */
export const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  gap: 6px;
`;

/* nome do aluno em destaque (primeiro <Span> dentro do hero) */
export const Span = styled.span`
  display: block;
  font-size: 14px;
  word-break: break-word;
  color: inherit;
`;

export const DivButtomEdit = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  width: 100%;
`;

export const DivTopActions = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  p {
    margin: 0;
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(4px);
    color: #fff;
    font-weight: 700;
    font-size: 12.5px;
    padding: 7px 14px;
    border-radius: 999px;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.12s ease;
    &:hover { background: rgba(255, 255, 255, 0.26); transform: translateY(-1px); }
  }
`;

/* divisor escola (laranja/azul) — mantido, mas suavizado */
export const SchoolDivider = styled.div`
  width: 100%;
  height: 3px;
  margin: 8px 0;
  background: linear-gradient(to right, ${CORAL} 50%, ${BLUE} 50%);
  border-radius: 2px;
  opacity: 0.85;
`;

/* ── botões ── */
export const Btt02 = styled.button`
  ${btnBase}
  background: ${TEAL_SOFT};
  color: ${TEAL_TEXT};
  border: 1px solid var(--teal-soft);
`;

export const Btt01 = styled.button`
  ${btnBase}
  background: ${RED};
  color: #fff;
`;

export const Button = styled.button`
  ${btnBase}
  background: ${TEAL};
  color: #fff;
  box-shadow: 0 2px 8px ${TEAL_GLOW};
  width: 100%;
  max-width: 300px;
  padding: 12px 20px;
  @media (max-width: ${bp.small}) { max-width: 100%; }
`;

export const ButtonRemove = styled.button`
  ${btnBase}
  background: ${RED};
  color: #fff;
`;

export const ButtonSecondary = styled.button`
  ${btnBase}
  background: var(--surface-2);
  color: ${GRAY_TEXT};
  border: 1px solid ${GRAY_LINE};
`;

export const ButtonCancel = styled.button`
  ${btnBase}
  background: ${RED_SOFT};
  color: ${RED};
  border: 1px solid #f1cdcd;
  margin-top: 6px;
  padding: 12px 22px;
`;

export const AddHistoricButton = styled.button`
  ${btnBase}
  background: ${TEAL};
  color: #fff;
  font-size: 12.5px;
  padding: 8px 14px;
`;

export const DivNewEmp = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  @media (max-width: ${bp.small}) { justify-content: center; }
`;

/* ── blocos de formulário (Boletim / Relatório / Notas) ── */
export const Input = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  animation: ${fadeUp} 0.35s ease backwards;
  transition: box-shadow 0.18s ease;
  &:hover { box-shadow: 0 5px 16px rgba(0, 0, 0, 0.05); }
  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${TEAL_TEXT};
    display: flex;
    align-items: center;
    gap: 8px;
  }
  @media (max-width: ${bp.mobile}) { padding: 14px; border-radius: 12px; }
`;

export const Label = styled.label`
  font-weight: 600;
  color: ${GRAY_TEXT};
  font-size: 13px;
`;

export const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 11px;
  font-size: 14px;
  font-family: inherit;
  color: var(--text-2);
  background: ${GRAY_BG};
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus { outline: none; border-color: ${TEAL_LIGHT}; box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.12); }
`;

export const ErrorMessage = styled.div`
  color: ${RED};
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  &::before { content: '⚠️'; }
`;

/* ── calendário ── */
export const ContainerCalendar = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  padding: 16px;
  animation: ${fadeUp} 0.35s ease backwards;
  @media (max-width: ${bp.mobile}) { padding: 12px; border-radius: 12px; }
`;

export const HistoricGradeContainer = styled.div`
  width: 100%;
`;

/* ── tabelas (mantidas por compatibilidade) ── */
export const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;
export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--surface);
  border-radius: 8px;
  overflow: hidden;
`;
export const TableHeader = styled.thead`background: ${GRAY_BG};`;
export const TableCell = styled.td`
  padding: 10px;
  border-bottom: 1px solid ${GRAY_LINE};
  vertical-align: middle;
  word-break: break-word;
`;
export const TableCellSmall = styled.td`padding: 8px; font-size: 14px;`;
export const TableBody = styled.tbody`display: table-row-group; width: 100%;`;

export const TableRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: stretch;
  gap: 12px;
  h3 { margin: 0; font-size: 17px; color: ${TEAL_TEXT}; text-align: center; }
  p { margin: 0; }
  label { font-size: 13px; font-weight: 600; color: ${GRAY_TEXT}; }
  input[type="date"] {
    padding: 11px 13px;
    border: 1px solid ${GRAY_LINE};
    border-radius: 11px;
    font-family: inherit;
    font-size: 14px;
    background: ${GRAY_BG};
    width: 100%;
    box-sizing: border-box;
  }
  .botões {
    display: flex;
    gap: 12px;
    justify-content: center;
    margin-top: 6px;
    @media (max-width: ${bp.small}) { flex-direction: column; & > button { width: 100%; max-width: 100%; } }
  }
`;

/* ── chips de status ── */
const chipColor = (v) =>
    v === 'ativo' ? css`background: ${GREEN_SOFT}; color: ${GREEN};`
        : v === 'transferido' ? css`background: ${AMBER_SOFT}; color: ${AMBER};`
            : v === 'concluido' ? css`background: ${BLUE_SOFT}; color: ${BLUE};`
                : v === 'inativo' ? css`background: ${RED_SOFT}; color: ${RED};`
                    : css`background: ${TEAL_SOFT}; color: ${TEAL_TEXT};`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  ${({ color }) => chipColor(color)}
  &::before { content: '●'; font-size: 8px; }
`;

export const StatusChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 13px;
  ${({ status }) => chipColor(status)}
`;

export const SelectStatus = styled.select`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${GRAY_LINE};
`;

export const MenuItem = styled.div`
  padding: 8px 14px;
  border-radius: 8px;
  transition: background .12s;
  cursor: pointer;
  &:hover { background: ${GRAY_BG}; }
`;

/* ── overlays / modais (padrão do design system) ── */
const overlayBase = css`
  position: fixed;
  inset: 0;
  background: rgba(0, 30, 38, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: ${fadeIn} 0.15s ease;
  overscroll-behavior: contain;
  @media (max-width: ${bp.mobile}) { padding: 0; align-items: flex-end; }
`;
const modalCardBase = css`
  background: var(--surface);
  border-radius: 16px;
  width: 100%;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
  animation: ${expand} 0.2s ease;
  max-height: 88vh;
  overflow-y: auto;
  @media (max-width: ${bp.mobile}) { border-radius: 18px 18px 0 0; }
`;

export const ModalOverlay = styled.div`${overlayBase}`;
export const ModalContent = styled.div`
  ${modalCardBase}
  max-width: 540px;
  padding: 24px;
  box-sizing: border-box;
  h3 { margin: 0 0 12px; font-size: 18px; color: ${TEAL_TEXT}; }
  p { margin: 0; color: ${GRAY_TEXT}; font-size: 14px; line-height: 1.55; }
  @media (max-width: ${bp.mobile}) { padding: 22px 18px; }
`;

export const BlurBackground = styled.div`${overlayBase}`;
export const ModalContainer = styled.div`
  ${modalCardBase}
  max-width: 420px;
  padding: 26px 24px;
  text-align: center;
  box-sizing: border-box;
  h3 { margin: 0 0 20px; font-size: 17px; color: ${TEAL_TEXT}; }
  div { display: flex; justify-content: center; gap: 12px; }
  button {
    ${btnBase}
    padding: 10px 22px;
    background: ${GRAY_BG};
    color: ${GRAY_TEXT};
    border: 1px solid ${GRAY_LINE};
  }
  @media (max-width: ${bp.mobile}) { padding: 24px 18px; }
`;

export const HistoricModalOverlay = styled.div`${overlayBase}`;
export const HistoricModalContent = styled.div`
  ${modalCardBase}
  max-width: 540px;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
  h3 { margin: 0; font-size: 18px; color: ${TEAL_TEXT}; }
  h4 { margin: 0; font-size: 13px; color: ${GRAY_TEXT}; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }
  input {
    padding: 12px 14px;
    border: 1px solid ${GRAY_LINE};
    border-radius: 11px;
    font-family: inherit;
    font-size: 14px;
    background: ${GRAY_BG};
    width: 100%;
    box-sizing: border-box;
    &:focus { outline: none; border-color: ${TEAL_LIGHT}; box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.12); }
  }
  @media (max-width: ${bp.mobile}) { padding: 22px 18px; }
`;

export const HistoricModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const HistoricGradeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 11px;
  background: ${GRAY_BG};
  span { font-size: 14px; font-weight: 600; color: var(--text-2); }
  strong { font-size: 15px; color: ${TEAL_TEXT}; }
  button { padding: 7px 12px; font-size: 12px; }
  @media (max-width: ${bp.small}) {
    grid-template-columns: 1fr auto;
    & > strong { grid-column: 2; text-align: right; }
  }
`;

export const EmptyHistoricMessage = styled.p`
  text-align: center;
  color: ${GRAY_TEXT};
  font-size: 14px;
  padding: 24px 12px;
  border: 1px dashed ${GRAY_LINE};
  border-radius: 11px;
  background: ${GRAY_BG};
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
  @media (max-width: ${bp.small}) { flex-direction: column-reverse; & > button { width: 100%; max-width: 100%; } }
`;

/* ── edição de nota ── */
export const EditOverlay = styled.div`${overlayBase}`;
export const EditContainer = styled.div`
  ${modalCardBase}
  max-width: 400px;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
  h3 { margin: 0; font-size: 17px; color: ${TEAL_TEXT}; text-align: center; }
  @media (max-width: ${bp.mobile}) { padding: 22px 18px; }
`;

export const EmpEdit = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  border-radius: 11px;
  background: ${GRAY_BG};
  border: 1px solid ${GRAY_LINE};
`;

export const Grade = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  .nota {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    justify-content: center;
  }
  p { margin: 0; font-weight: 700; color: ${GRAY_TEXT}; }
  input {
    width: 120px;
    padding: 11px 13px;
    border: 1px solid ${GRAY_LINE};
    border-radius: 11px;
    font-family: inherit;
    font-size: 15px;
    text-align: center;
    background: var(--surface);
    &:focus { outline: none; border-color: ${TEAL_LIGHT}; box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.12); }
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

/* ── avisos inline ── */
export const AddMatterSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
export const WarningBox = styled.div`
  padding: 14px 16px;
  background: ${AMBER_SOFT};
  border: 1px solid #f0d9a8;
  border-left: 4px solid ${AMBER};
  border-radius: 10px;
  color: #7a5310;
  font-size: 14px;
`;
export const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  @media (max-width: ${bp.small}) { justify-content: center; }
`;

/* ── skeleton ── */
const skeletonBase = css`
  background: linear-gradient(90deg, var(--skel-a) 0%, var(--skel-b) 50%, var(--skel-a) 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.3s linear infinite;
  border-radius: 8px;
`;
export const SkeletonLine = styled.div`
  ${skeletonBase}
  height: ${({ $h }) => $h || '14px'};
  width: ${({ $w }) => $w || '100%'};
  margin: 4px 0;
`;
export const SkeletonCard = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
export const SkeletonHero = styled.div`
  ${skeletonBase}
  height: 240px;
  border-radius: 16px;
  background: linear-gradient(90deg, #dde7ea 0%, #e9f0f2 50%, #dde7ea 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.3s linear infinite;
`;

/* compat: nomes legados ainda exportados */
export const List = styled.ul`
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
export const Search = styled.div`
  width: 100%;
  display: flex;
  gap: 14px;
  background: var(--surface);
  padding: 14px;
  border-radius: 12px;
  border: 1px solid ${GRAY_LINE};
`;
export const FormSearch = styled.form`display: flex; flex-direction: column; width: 100%;`;
export const FormFilter = styled.form`display: flex; flex-direction: column; width: 100%; max-width: 320px;`;
export const AreaEmp = styled.div`display: flex; width: 100%; margin-top: 6px;`;
export const InputEmp = styled.input`
  width: 100%;
  padding: 11px 13px;
  border-radius: 10px;
  border: 1px solid ${GRAY_LINE};
  font-size: 14px;
  box-sizing: border-box;
  background: var(--surface);
  &:focus { outline: none; border-color: ${TEAL_LIGHT}; box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.12); }
`;

/* grade de detalhes para o perfil (novo) */
export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 10px;
`;
export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 11px;
  padding: 9px 13px;
  backdrop-filter: blur(4px);
  min-width: 0;
  .k { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; opacity: 0.72; }
  .v { font-size: 13.5px; font-weight: 600; word-break: break-word; }
  ${({ $tone }) => $tone === 'green' && css`.v { color: #c8f7da; }`}
  ${({ $tone }) => $tone === 'red' && css`.v { color: #ffd2cc; }`}
  ${({ $tone }) => $tone === 'blue' && css`.v { color: #cfe3ff; }`}
`;
export const EmpName = styled.h1`
  margin: 0;
  font-size: 23px;
  font-weight: 800;
  letter-spacing: 0.2px;
  word-break: break-word;
  @media (max-width: ${bp.mobile}) { font-size: 19px; }
  @media (max-width: ${bp.small}) { font-size: 17px; }
`;
export const ClassChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  span {
    background: ${TEAL_SOFT};
    color: ${TEAL_TEXT};
    border: 1px solid var(--teal-soft);
    border-radius: 10px;
    padding: 8px 13px;
    font-size: 13px;
    font-weight: 600;
  }
`;
export const ClassTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${TEAL_TEXT};
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* \xe2\x94\x80\xe2\x94\x80 formul\xc3\xa1rio de cadastro de respons\xc3\xa1vel \xe2\x94\x80\xe2\x94\x80 */
export const RespForm = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
`;

export const RespFormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  input {
    width: 100%;
    box-sizing: border-box;
    padding: 12px 14px;
    border: 1px solid ${GRAY_LINE};
    border-radius: 11px;
    font-size: 14px;
    font-family: inherit;
    color: var(--text-2);
    background: ${GRAY_BG};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      outline: none;
      border-color: ${TEAL_LIGHT};
      box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.12);
    }
  }
`;
/* ── lista de responsáveis ── */
export const RespList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
`;

export const RespCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px 16px 20px;
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 16px;
  overflow: hidden;
  transition: box-shadow 0.18s ease, transform 0.12s ease, border-color 0.18s ease;

  /* faixa lateral teal */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(to bottom, ${TEAL}, ${TEAL_LIGHT});
  }

  &:hover {
    box-shadow: 0 8px 22px rgba(0, 62, 79, 0.1);
    transform: translateY(-2px);
    border-color: ${TEAL_SOFT};
  }
`;

export const RespCardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .resp-id {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    flex: 1;
    strong {
      color: ${TEAL_TEXT};
      font-size: 15.5px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .kin {
      align-self: flex-start;
      font-size: 11px;
      font-weight: 700;
      color: ${CORAL};
      background: rgba(224, 122, 95, 0.12);
      padding: 2px 10px;
      border-radius: 999px;
      text-transform: capitalize;
      letter-spacing: 0.2px;
    }
  }
`;

export const RespAvatar = styled.div`
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 16px;
  color: #fff;
  text-transform: uppercase;
  background: linear-gradient(135deg, ${TEAL}, ${TEAL_LIGHT});
  box-shadow: 0 3px 10px ${TEAL_GLOW};
`;

export const RespEditBtn = styled.button`
  flex-shrink: 0;
  border: 1px solid var(--teal-soft);
  background: ${TEAL_SOFT};
  color: ${TEAL_TEXT};
  font-weight: 700;
  font-size: 12.5px;
  padding: 7px 14px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, transform 0.12s ease;
  &:hover { background: #d8e8ed; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

export const RespCardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

export const RespDataPill = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  background: ${GRAY_BG};
  border: 1px solid ${GRAY_LINE};
  border-radius: 11px;
  min-width: 0;

  .icon {
    font-size: 15px;
    line-height: 1;
    flex-shrink: 0;
  }
  .data {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .k {
    font-size: 10.5px;
    font-weight: 700;
    color: ${GRAY_TEXT};
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .v {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--text-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const RespRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 13.5px;
  padding: 3px 0;
  border-top: 1px dashed ${GRAY_LINE};
  &:first-of-type { border-top: none; }

  .k { color: ${GRAY_TEXT}; font-weight: 600; }
  .v { color: var(--text-2); font-weight: 600; text-align: right; word-break: break-word; }
`;
/* ── verificação de CPF (status abaixo do campo) ── */
export const CpfStatus = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  margin-top: 2px;
  ${({ $tone }) =>
    $tone === 'found'
      ? `color: ${CORAL}; cursor: pointer; text-decoration: underline;`
      : `color: ${GRAY_TEXT};`}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &::before {
    content: ${({ $tone }) => ($tone === 'found' ? "'⚠️'" : "'⏳'")};
  }
`;

/* ── card do responsável já existente ── */
export const RespExistCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 14px 16px;
  background: ${GRAY_BG};
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  margin: 6px 0;

  .resp-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 4px;
  }
  .resp-id {
    display: flex;
    flex-direction: column;
    gap: 2px;
    strong { color: ${TEAL_TEXT}; font-size: 15px; }
    .kin {
      align-self: flex-start;
      font-size: 11.5px;
      font-weight: 700;
      color: ${CORAL};
      background: rgba(224, 122, 95, 0.12);
      padding: 2px 9px;
      border-radius: 999px;
      text-transform: capitalize;
    }
  }
`;

export const RespExistRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 13.5px;
  padding: 3px 0;
  border-top: 1px dashed ${GRAY_LINE};
  &:first-of-type { border-top: none; }

  .k { color: ${GRAY_TEXT}; font-weight: 600; }
  .v { color: var(--text-2); font-weight: 600; text-align: right; word-break: break-word; }
`;